import type { ReactNode } from 'react';
import type { BlockStyle, PriceBookContent } from '@/blocks/domain/types';
import { isPriceBookNumericColumn } from '@/blocks/domain/helpers/priceBookColumnKeys';
import { BORDER_COLORS, TEXT_COLORS } from '@/common/presentation/constants/colors';
import type { RendererProps } from '@/document-render/domain/RendererContract';
import { buildTableColumnWidthPercentages } from '@/document-render/domain/buildTableColumnWidthPercentages';
import { formatPriceBookCellValue } from '@/document-render/domain/formatDocumentMoneyCell';
import { resolvePriceBookRowsForBlock } from '@/document-render/domain/resolvePriceBookRowsForBlock';
import { blockFontSizeCss } from '@/document-render/application/blockFontSize';
import { MONEY_CELL_STYLE, MONEY_COLUMN_MIN_WIDTH } from '@/document-render/presentation/blocks/moneyCellStyles';

const TABLE_BODY_CELL_STYLE = {
  padding: '8px 10px',
  overflowWrap: 'break-word' as const,
  wordBreak: 'normal' as const
};

function flatTableFontSize(style: BlockStyle, isCompact: boolean) {
  const measured = blockFontSizeCss(style);
  if (typeof measured === 'string') return measured;
  if (isCompact) return Math.max(10, (style.fontSize ?? 14) - 1);
  return style.fontSize;
}

function resolveColStyle(isCompact: boolean, isMoney: boolean, width: number) {
  if (isCompact) {
    if (isMoney) return { minWidth: MONEY_COLUMN_MIN_WIDTH };
    return { minWidth: `${width}%` };
  }
  if (isMoney) return { width: `${width}%`, minWidth: MONEY_COLUMN_MIN_WIDTH };
  return { width: `${width}%` };
}

export function PriceBookFlatTable({
  content,
  context,
  style,
  slice,
  viewport,
  currency,
  cultureTag,
  header
}: RendererProps<PriceBookContent> & { currency: string; cultureTag: string; header: ReactNode }) {
  const isCompact = viewport?.compact === true;
  const priceBookRows = Array.isArray(context.priceBook) ? context.priceBook : [];
  const allItems = resolvePriceBookRowsForBlock(priceBookRows, content.serviceItemIds);
  const cols = [...(content.columns ?? [])].filter((column) => column.visible).sort((a, b) => a.order - b.order);
  const columnWidths = buildTableColumnWidthPercentages(
    cols.map((col) => col.key),
    isPriceBookNumericColumn
  );
  const start = slice?.startIndex ?? 0;
  const end = slice?.endIndex ?? allItems.length;
  const items = allItems.slice(start, end);
  const shouldShowHeader = !slice || slice.startIndex === 0 || slice.isContinuation;
  const minHeight = items.length * 22 + (shouldShowHeader ? 22 : 0);

  if (items.length === 0 && (!slice || slice.startIndex === 0)) {
    return (
      <section
        aria-label="block-price-book"
        style={{
          paddingTop: style.paddingY,
          paddingBottom: style.paddingY,
          color: style.textColor,
          background: style.backgroundColor,
          fontSize: flatTableFontSize(style, false),
          fontWeight: style.fontWeight
        }}
      >
        {content.title ? <div style={{ fontWeight: 700, marginBottom: 6 }}>{content.title}</div> : null}
        <div style={{ fontSize: 12, fontStyle: 'italic', color: TEXT_COLORS.MUTED }}>Rate schedule — no entries yet.</div>
      </section>
    );
  }

  return (
    <section
      aria-label="block-price-book"
      style={{
        minHeight,
        paddingTop: style.paddingY,
        paddingBottom: style.paddingY,
        color: style.textColor,
        background: style.backgroundColor,
        fontSize: flatTableFontSize(style, isCompact),
        fontWeight: style.fontWeight
      }}
    >
      {content.title ? <div style={{ fontWeight: 700, marginBottom: 6 }}>{content.title}</div> : null}
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: isCompact ? 'auto' : 'fixed' }}>
        <colgroup>
          {columnWidths.map((width, index) => {
            const key = cols[index]!.key;
            return <col key={key} style={resolveColStyle(isCompact, isPriceBookNumericColumn(key), width)} />;
          })}
        </colgroup>
        {shouldShowHeader ? header : null}
        <tbody>
          {items.map((item, idx) => (
            <tr key={slice ? `${slice.startIndex + idx}` : `${idx}`} style={{ borderBottom: `1px solid ${BORDER_COLORS.LIGHT}` }}>
              {cols.map((col) => {
                const value = (item as Record<string, unknown>)[col.key];
                const text = formatPriceBookCellValue(value, col.key, currency, cultureTag);
                const isMoney = isPriceBookNumericColumn(col.key);
                return (
                  <td
                    key={col.key}
                    style={{
                      ...TABLE_BODY_CELL_STYLE,
                      ...(isMoney ? MONEY_CELL_STYLE : {}),
                      padding: isCompact ? '6px 8px' : TABLE_BODY_CELL_STYLE.padding,
                      minWidth: isMoney ? MONEY_COLUMN_MIN_WIDTH : undefined,
                      textAlign: isMoney ? 'right' : 'left'
                    }}
                  >
                    {text}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
